import { FlexRow } from '@/components/common/Flex';
import { hoverStyleProps } from '@/components/common/styleHelpers';
import { extractTextFromElement } from '@/util/react';
import { BoxProps, Tooltip } from '@chakra-ui/react';
import { Button } from './Button';

type ColorScheme = 'gray' | 'blue';

export interface PillSwitcherProps<T extends string>
    extends Omit<BoxProps, 'onChange'> {
    options: {
        label: string | JSX.Element;
        value: T;
        tooltip?: string;
        tooltipPlacement?: 'top' | 'bottom';
        disabled?: boolean;
    }[];
    onChange: (value: T) => void;
    currentValue: T;
    colorScheme?: ColorScheme;
}

export const PillSwitcher = <T extends string>({
    options,
    onChange,
    currentValue,
    colorScheme = 'gray',
    ...props
}: PillSwitcherProps<T>) => (
    <FlexRow {...props} wrap="nowrap">
        {options.map((option) => {
            const colors = getColors({
                colorScheme,
                isSelected: currentValue === option.value,
            });

            return (
                <Tooltip
                    label={option.tooltip}
                    key={option.value}
                    placement={option.tooltipPlacement || 'bottom'}
                >
                    <Button
                        isDisabled={option.disabled}
                        variant="pill"
                        size="sm"
                        h={7}
                        bg={colors.bg}
                        color={colors.color}
                        border="none"
                        mr={1}
                        onClick={() => {
                            if (option.disabled) return;
                            onChange(option.value);
                        }}
                        onClickEvent="Pill Switcher Button Clicked"
                        onClickEventParams={{
                            label: extractTextFromElement(option.label),
                        }}
                        _last={{ mr: 0 }}
                        _disabled={colors.disabled}
                        {...(option.disabled
                            ? { _hover: {} }
                            : hoverStyleProps(colors.hover))}
                    >
                        {option.label}
                    </Button>
                </Tooltip>
            );
        })}
    </FlexRow>
);

const getColors = ({
    colorScheme,
    isSelected,
}: {
    colorScheme: ColorScheme;
    isSelected: boolean;
}) => {
    if (colorScheme === 'gray') {
        return {
            bg: isSelected ? 'gray.50' : 'transparent',
            color: isSelected ? '' : 'gray.400',
            disabled: {
                bg: 'transparent',
                color: 'gray.300',
            },
            hover: {
                bg: 'gray.50',
            },
        };
    }

    // blue
    return {
        bg: isSelected ? 'blue.500' : 'transparent',
        color: isSelected ? 'white' : 'gray.400',
        disabled: {
            bg: 'transparent',
            color: 'gray.300',
        },
        hover: {
            color: isSelected ? 'white' : 'gray.400',
            bg: isSelected ? 'blue.500' : 'gray.50',
        },
    };
};
